import {
  BenefitType,
  Maybe,
  ParkZoneBenefit,
} from 'types/generatedSchemaTypes';

export const PERMIT_GRANTING_BENEFIT_TYPES: BenefitType[] = [
  'USE_PMC_AUTOMATIC',
  'USE_POOL',
  'USE_LOYALTY_PMC',
  'USE_MEMBER_GROUP_LTP',
];

export type StaffMemberAdmissionType =
  | 'UNLOCK_CODE_MATCH'
  | 'HSA_EMPLOYEE_UNIT_MATCH';

const isPermitGrantingBenefitType = (
  benefitTargetType?: Maybe<BenefitType>
): boolean =>
  !!benefitTargetType &&
  PERMIT_GRANTING_BENEFIT_TYPES.includes(benefitTargetType);

const hasAdmissionType = (
  benefit: ParkZoneBenefit,
  admissionType: StaffMemberAdmissionType
): boolean =>
  Array.isArray(benefit.admissionTypes) &&
  benefit.admissionTypes.includes(admissionType);

export const isStaffMemberBenefit = (
  benefit: ParkZoneBenefit,
  memberGroupUids: string[]
): boolean => {
  if (!benefit.memberGroupUid) {
    return false;
  }
  if (memberGroupUids.includes(benefit.memberGroupUid)) {
    return false;
  }
  if (!isPermitGrantingBenefitType(benefit.benefitTargetType)) {
    return false;
  }
  return (
    hasAdmissionType(benefit, 'UNLOCK_CODE_MATCH') ||
    hasAdmissionType(benefit, 'HSA_EMPLOYEE_UNIT_MATCH')
  );
};

/** Prefer unlock-code when that path is enabled; otherwise fall back to HSA. */
export const getStaffMemberAdmission = (
  benefit: ParkZoneBenefit,
  enabledAdmissions: StaffMemberAdmissionType[] = [
    'UNLOCK_CODE_MATCH',
    'HSA_EMPLOYEE_UNIT_MATCH',
  ]
): StaffMemberAdmissionType | undefined => {
  if (
    enabledAdmissions.includes('UNLOCK_CODE_MATCH') &&
    hasAdmissionType(benefit, 'UNLOCK_CODE_MATCH')
  ) {
    return 'UNLOCK_CODE_MATCH';
  }
  if (
    enabledAdmissions.includes('HSA_EMPLOYEE_UNIT_MATCH') &&
    hasAdmissionType(benefit, 'HSA_EMPLOYEE_UNIT_MATCH')
  ) {
    return 'HSA_EMPLOYEE_UNIT_MATCH';
  }
  return undefined;
};

/**
 * Pick an eligible staff-member benefit whose admission path is enabled.
 * Prefer unlock-code over HSA when both are available and enabled.
 */
export const findStaffMemberBenefit = (
  benefits: Maybe<ParkZoneBenefit>[],
  memberGroupUids: string[],
  enabledAdmissions: StaffMemberAdmissionType[]
): ParkZoneBenefit | undefined => {
  if (enabledAdmissions.length === 0) {
    return undefined;
  }

  const eligible = benefits.filter(
    (b): b is ParkZoneBenefit => !!b && isStaffMemberBenefit(b, memberGroupUids)
  );

  if (enabledAdmissions.includes('UNLOCK_CODE_MATCH')) {
    const unlockBenefit = eligible.find((b) =>
      hasAdmissionType(b, 'UNLOCK_CODE_MATCH')
    );
    if (unlockBenefit) {
      return unlockBenefit;
    }
  }

  if (enabledAdmissions.includes('HSA_EMPLOYEE_UNIT_MATCH')) {
    return eligible.find((b) => hasAdmissionType(b, 'HSA_EMPLOYEE_UNIT_MATCH'));
  }

  return undefined;
};
